import { useState } from "react"
import { router, usePage } from "@inertiajs/react"
import { format, startOfDay, isBefore } from "date-fns"
import Calendar from "react-calendar"
import { Link } from "@inertiajs/react"
import { Zap } from "lucide-react"
import { PageHeader } from "@/components/core/page_header"
import Layout from "@/pages/layouts/application"
import { cn } from "@/lib/utils"
import contentApi from "@/api/Organizations/ContentApi"
import postsApi from "@/api/Organizations/PostsApi"
import { Button } from "@/components/ui/button"
import 'react-calendar/dist/Calendar.css'

// Custom styles for react-calendar
// Define today at the top level for reuse
const today = startOfDay(new Date());
const calendarStyles = `
  .react-calendar {
    width: 100%;
    border: none;
    background: transparent;
    color: hsl(var(--foreground));
  }
  .react-calendar__navigation {
    margin-bottom: 1rem;
  }
  .react-calendar__navigation button {
    min-width: auto;
    background: transparent !important;
    font-size: 14px;
    padding: 8px;
    color: hsl(var(--foreground));
  }
  .react-calendar__navigation button:disabled {
    background: transparent !important;
    opacity: 0.5;
  }
  .react-calendar__navigation__label,
  .react-calendar__navigation__label:enabled,
  .react-calendar__navigation__label:enabled:focus,
  .react-calendar__navigation__label:enabled:focus-visible,
  .react-calendar__navigation__label:enabled:active,
  .react-calendar__navigation__label:disabled {
    background: transparent !important;
  }
  .react-calendar__navigation button:enabled:hover {
    background-color: hsl(var(--accent));
    border-radius: 6px;
  }
  .react-calendar__navigation button:enabled:focus:not(:hover),
  .react-calendar__navigation button:enabled:focus-visible:not(:hover) {
    background-color: transparent;
  }
  .react-calendar__navigation button:enabled:active {
    background-color: transparent;
  }
  .react-calendar__month-view__weekdays {
    text-align: center;
    text-transform: uppercase;
    font-size: 0.75em;
    padding: 8px 0;
    color: hsl(var(--muted-foreground));
  }
  .react-calendar__month-view__weekdays__weekday {
    color: hsl(var(--muted-foreground));
  }
  .react-calendar__month-view__days__day {
    padding: 8px;
    background: none;
    color: hsl(var(--foreground));
  }
  .react-calendar__month-view__days__day--weekend {
    color: hsl(var(--foreground));
  }
  .react-calendar__tile {
    height: 120px;
    border: 1px solid hsl(var(--border));
    margin: -1px 0 0 -1px;
  }
  .react-calendar__tile:enabled:hover,
  .react-calendar__tile:enabled:focus {
    background-color: hsl(var(--accent) / 0.5);
    border-radius: 8px;
  }
  .react-calendar__tile--now {
    background: transparent;
    box-shadow: none;
    border-radius: 8px;
  }
  .react-calendar__tile--now:enabled:hover {
    background-color: hsl(var(--accent) / 0.5);
    border-radius: 8px;
  }
  .react-calendar__tile--now.react-calendar__tile--active:enabled:hover,
  .react-calendar__tile--now.react-calendar__tile--active:enabled:focus {
    background-color: hsl(var(--accent) / 0.5);
    color: hsl(var(--foreground));
  }
  .react-calendar__month-view .react-calendar__tile--now abbr {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 1.9rem;
    height: 1.9rem;
    background: #fb5050;
    border-radius: 9999px;
    transition: background-color 500ms ease-in-out;
  }
  .react-calendar__month-view .react-calendar__tile--now:enabled:hover abbr {
    background: transparent;
  }
  .react-calendar__tile--active {
    background: hsl(var(--primary));
    color: hsl(var(--primary-foreground));
    border-radius: 8px;
  }
  .react-calendar__tile--active:enabled:hover,
  .react-calendar__tile--active:enabled:focus {
    background: hsl(var(--primary));
    border-radius: 8px;
  }
  /* Hide scrollbar but keep functionality */
  .calendar-day-content {
    scrollbar-width: none; /* Firefox */
    -ms-overflow-style: none; /* IE and Edge */
  }
  .calendar-day-content::-webkit-scrollbar {
    display: none; /* Chrome, Safari and Opera */
  }
  .future-date-tile {
    cursor: pointer;
  }
  /* Style for disabled (past) dates */
  .react-calendar__tile:disabled {
    background-color: transparent !important; /* Ensure no overriding background */
    opacity: 0.5; /* Make it faded */
    cursor: not-allowed; /* Indicate non-interactive */
  }
  /* Hide ghost post by default */
  .ghost-post {
    opacity: 0;
    transform: translateY(2px);
    pointer-events: none;
    transition: opacity 300ms ease-in-out, transform 300ms ease-in-out;
  }
  /* Show ghost post on calendar tile hover */
  .react-calendar__tile:hover .ghost-post {
    opacity: 1;
    transform: translateY(0);
    pointer-events: auto;
  }
  .react-calendar abbr {
    text-decoration: none;
  }
`

const POST_DELIMITER = "=============";

const getPostStyles = (status) => {
  switch (status) {
    case "approved":
      return "bg-green-500/20 text-green-500"
    case "published":
      return "bg-green-500/20 text-green-500"
    case "publishing":
      return "bg-green-500/20 text-green-500"
    case "draft":
      return "bg-gray-500/20 text-gray-300"
    case "pending":
      return "bg-yellow-500/20 text-yellow-500"
    case "rejected":
      return "bg-red-500/20 text-red-500"
    case "error":
      return "bg-red-500/20 text-red-500"
    default:
      return "bg-gray-500/20 text-gray-300"
  }
}

const PostItem = ({ post, href, isGhost }) => {
  const content = post?.twitter_content?.replace(new RegExp(POST_DELIMITER, 'g'), ' ') || ''

  return (
    <Link
      href={href}
      className={cn(
        "block w-full text-xs p-2 rounded cursor-pointer hover:opacity-80 text-left",
        isGhost
          ? "ghost-post bg-gray-500/20 text-gray-300"
          : getPostStyles(post.status)
      )}
      onClick={(e) => {
        e.stopPropagation()
      }}>
      {isGhost ? "New post" : content.substring(0, 30) + (content.length > 30 ? "..." : "")}
    </Link>
  )
}

export default function Show({ posts }) {
  const { organization } = usePage().props.auth
  const [date, setDate] = useState(new Date())

  const handleMonthChange = (newDate) => {
    router.get(
      contentApi.show.path({ org_nano_id: organization.nano_id }),
      { month: format(newDate, "yyyy-MM-dd") },
      { preserveState: true }
    )
  }

  const handleDayClick = (value, event) => {
    const formattedDate = format(new Date(value), "yyyy-MM-dd")

    router.get(
      postsApi.new.path({ org_nano_id: organization.nano_id }),
      { scheduled_at: formattedDate }
    )
  }

  const groupPostsByDate = (posts) => {
    const grouped = {}
    posts.forEach(post => {
      const date = format(new Date(post.scheduled_at), "yyyy-MM-dd")
      if (!grouped[date]) {
        grouped[date] = []
      }
      grouped[date].push(post)
    })
    return grouped
  }

  const groupedPosts = groupPostsByDate(posts)

  const tileContent = ({ date: day, view }) => {
    if (view !== 'month') return null

    const dateStr = format(day, "yyyy-MM-dd")
    const dayPosts = groupedPosts[dateStr] || []
    const showGhostPost = !isBefore(startOfDay(day), today) && dayPosts.length === 0

    return (
      <div className="h-full w-full flex flex-col items-stretch mt-1">
        <div className="w-full overflow-y-auto max-h-[80px] space-y-1.5 px-1 calendar-day-content">
          {dayPosts.map((post, index) => (
            <PostItem
              key={post.id || index}
              post={post}
              href={postsApi.show.path({ org_nano_id: organization.nano_id, id: post.id })}
            />
          ))}
          {showGhostPost && (
            <PostItem
              isGhost
              post={null}
              href={postsApi.new.path({ org_nano_id: organization.nano_id }) + `?scheduled_at=${dateStr}`}
            />
          )}
        </div>
      </div>
    )
  }

  // Add custom class to tiles for future dates to indicate clickability
  const tileClassName = ({ date, view }) => {
    if (view === 'month') {
      const day = startOfDay(date);
      if (!isBefore(day, today)) {
        return 'cursor-pointer future-date-tile'; // Add your custom class here
      }
    }
    return null;
  };

  return (
    <Layout>
      <PageHeader title="Content Calendar">
        <div className="flex items-center gap-2">
          <Button size="sm" variant="secondary" onClick={() => router.get(`/${organization.nano_id}/content/list_view`)}>
            List View
          </Button>
          <Button size="sm" variant="default" onClick={() => router.get(postsApi.new.path({ org_nano_id: organization.nano_id }))}>
            New Post
          </Button>
        </div>
      </PageHeader>

      <div className="rounded-lg border bg-card text-card-foreground shadow-sm p-4">
        <style>{calendarStyles}</style>
        <Calendar
          value={date}
          onChange={setDate}
          onClickDay={handleDayClick}
          onActiveStartDateChange={({ activeStartDate }) => handleMonthChange(activeStartDate)}
          className="w-full"
          tileContent={tileContent}
          tileClassName={tileClassName}
          tileDisabled={({ date: tileDate, view }) => view === 'month' && isBefore(startOfDay(tileDate), today)}
          showNavigation={true}
          prevLabel="Previous month"
          nextLabel="Next month"
          navigationLabel={({ date }) => format(date, 'MMMM yyyy')}
        />
      </div>
    </Layout>
  )
}
